import React, {Fragment, useState} from 'react';

import {useDispatch, useSelector} from 'react-redux';
import {UncontrolledTooltip} from 'reactstrap';

import {
  selectIsHidingDeletedComments,
  toggleIsHidingDeletedComments,
} from 'app/comments/slice';
import {Mentionable} from 'app/components/general/Mentionable';
import StateButton from 'app/general/widgets/StateButton';
import {gt} from 'app/utils/i18n';

import type {CommentContentType} from 'sweet-types';

type Props = {
  onSubmit: (commentData: string) => Promise<unknown>;
  className?: string;
  buttonClass?: string;
  inputClass?: string;
  contentType: CommentContentType;
  objectId: string | number;
  hideTooltip?: boolean;
};

export const CommentForm = ({
  inputClass = '',
  buttonClass = '',
  className = '',
  onSubmit,
  contentType,
  objectId,
  hideTooltip,
  ...other
}: Props) => {
  const dispatch = useDispatch();

  const isHidingDeletedComments = useSelector(selectIsHidingDeletedComments);
  const [comment, setComment] = useState<string>('');
  const [idle, setIdle] = useState<boolean>(true);
  const [fieldRef, setFieldRef] = useState<null | HTMLElement>(null);

  const handleSubmit = (
    event:
      | React.SyntheticEvent<HTMLFormElement>
      | React.KeyboardEvent<HTMLDivElement>,
    currentComment?: string
  ) => {
    const newComment = currentComment != null ? currentComment : comment;
    event.preventDefault();
    setComment('');
    setIdle(false);
    onSubmit(newComment).then(
      () => setIdle(true),
      () => setIdle(true)
    );
  };

  const interceptRef = (ref: {current: null | HTMLElement}) => {
    const originalRef = ref;

    return (newFieldRef: null | HTMLElement) => {
      setFieldRef(newFieldRef);

      if (originalRef != null) {
        originalRef.current = newFieldRef;
      }
    };
  };

  return (
    <Mentionable
      contentType={contentType}
      objectId={objectId}
      onChange={(comment: string) => setComment(comment)}
      onSubmit={handleSubmit}
      value={comment}
    >
      {({Field, fieldProps, mentionsMenu}) => (
        <Fragment>
          {mentionsMenu}
          <form {...other} onSubmit={handleSubmit}>
            <div className="m-1 ml-2">
              <input
                type="checkbox"
                id="exclude_deleted_comments"
                className="mr-1 align-middle"
                checked={isHidingDeletedComments}
                onChange={() => dispatch(toggleIsHidingDeletedComments())}
              />
              <label htmlFor="exclude_deleted_comments" className="m-0">
                <small>Hide deleted comments</small>
              </label>
            </div>
            <div className={className}>
              <div className="input-group">
                <Field
                  className={`form-control rounded-0 border-left-0 ${inputClass}`}
                  autoFocus={true}
                  style={{
                    height: 'auto',
                    maxHeight: '8em',
                    overflow: 'auto',
                  }}
                  placeholder={gt('Leave a comment')}
                  {...fieldProps}
                  ref={interceptRef(fieldProps.ref)}
                />
                <div className="input-group-append">
                  <StateButton
                    style={{
                      boxShadow: 'none',
                    }}
                    className={`btn rounded-0 ${buttonClass}`}
                    busyContent={<i className="fa fa-circle-notch fa-spin" />}
                    disabled={!comment}
                    isIdle={idle}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    onClick={() => {}}
                    type="submit"
                  >
                    <i className="fa fa-paper-plane" />
                  </StateButton>
                </div>
              </div>
            </div>
            {fieldRef != null && comment === '' && hideTooltip !== true ? (
              <UncontrolledTooltip delay={250} target={fieldRef}>
                {gt('Use the @ symbol to mention a teammate')}
              </UncontrolledTooltip>
            ) : null}
          </form>
        </Fragment>
      )}
    </Mentionable>
  );
};
